import { notifyCreated, notifyDeleted, notifyError, notifyUpdated } from './notify.js';
import { isUserCommitTurn } from './user-commit-turn.js';

import type { HttpMethod, RequestOptions } from '@/shared/api/types.js';
import { translateError } from '@/shared/i18n/error-messages.js';

export type MutationFeedbackKind = 'created' | 'updated' | 'deleted';

export const SUPPORT_INCIDENT_TOAST = 'Não foi possível concluir. Entre em contato com o suporte.';

export type MutationFeedbackEvent =
  | { kind: MutationFeedbackKind }
  | { kind: 'error'; status: number; code: string; message: string };

let suppressDepth = 0;

export function mutationFeedbackKind(
  method: HttpMethod,
  options: Pick<RequestOptions, 'feedback' | 'skipAuth'>,
  userInitiated = isUserCommitTurn(),
): MutationFeedbackKind | null {
  if (
    options.skipAuth === true ||
    options.feedback === 'silent' ||
    options.feedback === 'error-only' ||
    !userInitiated
  ) {
    return null;
  }
  if (
    options.feedback === 'created' ||
    options.feedback === 'updated' ||
    options.feedback === 'deleted'
  ) {
    return options.feedback;
  }
  if (method === 'POST') {
    return 'created';
  }
  if (method === 'PATCH' || method === 'PUT') {
    return 'updated';
  }
  if (method === 'DELETE') {
    return 'deleted';
  }
  return null;
}

export function shouldEmitMutationError(
  status: number,
  feedbackKind: MutationFeedbackKind | null,
  userInitiated: boolean,
  options: Pick<RequestOptions, 'feedback' | 'skipAuth'>,
): boolean {
  if (suppressDepth > 0 || options.skipAuth === true || options.feedback === 'silent') {
    return false;
  }
  if (feedbackKind !== null) {
    return true;
  }
  // BR-WEB-22 — error-only surfaces failures like a toasted mutation would.
  if (options.feedback === 'error-only') {
    return userInitiated;
  }
  return userInitiated && status >= 500;
}

export function emitMutationFeedback(event: MutationFeedbackEvent): void {
  if (suppressDepth > 0) {
    return;
  }
  if (event.kind === 'error' && event.status >= 500) {
    notifyError(SUPPORT_INCIDENT_TOAST);
    return;
  }
  switch (event.kind) {
    case 'created':
      notifyCreated('Incluído.');
      return;
    case 'updated':
      notifyUpdated('Alteração salva.');
      return;
    case 'deleted':
      notifyDeleted('Excluído.');
      return;
    case 'error':
      notifyError(translateError(event.code, event.message));
      return;
  }
}

export async function withoutMutationFeedback<T>(work: () => Promise<T>): Promise<T> {
  suppressDepth += 1;
  try {
    return await work();
  } finally {
    suppressDepth -= 1;
  }
}
